import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Chip,
  Grid,
  CircularProgress,
  Autocomplete,
  Fade
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import SendIcon from '@mui/icons-material/Send'
import GroupIcon from '@mui/icons-material/Group'
import { itsmProductApi, awsCsgApi, productDlApi, awsCertAccountApi } from '../utils/api'
import TeamDistributionListsForm from '../components/TeamDistributionListsForm'

const AccountOnboard = ({ 
  preSelectedProduct = null, 
  useAvailableAccountsOnly = false, 
  onSuccess = null,
  isModal = false 
}) => {
  const [formData, setFormData] = useState({
    productCode: preSelectedProduct?.product_code || '',
    productName: preSelectedProduct?.product_name || '',
    awsAccountName: '',
    awsAccountNumber: '',
    applicationId: '',
    productEnv: '',
    developmentTeamDL: '',
    additionalDL: '',
  })
  const [itsmProducts, setItsmProducts] = useState(preSelectedProduct ? [preSelectedProduct] : [])
  const [awsAccounts, setAwsAccounts] = useState([])
  const [availableEnvironments, setAvailableEnvironments] = useState(['dev', 'qa', 'uat', 'prd'])
  const [disabledEnvironments, setDisabledEnvironments] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [productSearchText, setProductSearchText] = useState('')
  const [loadingAccounts, setLoadingAccounts] = useState(false)
  const [loadingTeamInfo, setLoadingTeamInfo] = useState(false)
  const [loadingDLSubmit, setLoadingDLSubmit] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [hasProductDLs, setHasProductDLs] = useState(true)
  const [generatedApiKey, setGeneratedApiKey] = useState('')
  const [generatedClientId, setGeneratedClientId] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  // Load ITSM products on component mount (skip if pre-selected)
  useEffect(() => {
    if (!preSelectedProduct) {
      fetchItsmProducts()
    }
  }, [preSelectedProduct])

  // Update form data when preSelectedProduct changes (for modal usage)
  useEffect(() => {
    if (preSelectedProduct) {
      setFormData(prev => ({
        ...prev,
        productCode: preSelectedProduct.product_code,
        productName: preSelectedProduct.product_name,
      }))
      setItsmProducts([preSelectedProduct])
    }
  }, [preSelectedProduct])

  // Debounced search for products (skip if pre-selected)
  useEffect(() => {
    if (preSelectedProduct) return // Don't search if product is pre-selected
    
    const timer = setTimeout(() => {
      if (productSearchText !== undefined) {
        fetchItsmProducts(productSearchText)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [productSearchText, preSelectedProduct])

  // Load AWS accounts when product is selected
  useEffect(() => {
    const productCode = formData.productCode || preSelectedProduct?.product_code
    if (productCode) {
      loadAwsAccountsForProduct(productCode)
    } else {
      setAwsAccounts([])
    }
  }, [formData.productCode, useAvailableAccountsOnly, preSelectedProduct])

  const fetchItsmProducts = async (search = '') => {
    setLoadingProducts(true)
    try {
      const response = await itsmProductApi.getAll({ search, limit: 100 })
      setItsmProducts(response.data.items || [])
    } catch (error) {
      console.error('Error loading ITSM products:', error)
      setSnackbar({ open: true, message: 'Error loading products', severity: 'error' })
    } finally {
      setLoadingProducts(false)
    }
  }

  const loadAwsAccountsForProduct = async (productCode) => {
    try {
      setLoadingAccounts(true)
      let response
      if (useAvailableAccountsOnly) {
        response = await awsCertAccountApi.getAvailableAccountsByProduct(productCode)
      } else {
        response = await awsCsgApi.getByProduct(productCode)
      }
      const accounts = response.data.items || response.data || []
      setAwsAccounts(accounts)
    } catch (error) {
      console.error('Error loading AWS accounts:', error)
      setSnackbar({ open: true, message: 'Error loading AWS accounts for this product', severity: 'error' })
      setAwsAccounts([])
    } finally {
      setLoadingAccounts(false)
    }
  }

  const determineAvailableEnvironments = (accountName, onboardedEnvironments = []) => {
    const lowerAccountName = accountName.toLowerCase()
    
    if (lowerAccountName.includes('-dev') || lowerAccountName.includes('_dev')) {
      return { available: ['dev'], disabled: [] }
    } else if (lowerAccountName.includes('-pro') || lowerAccountName.includes('_pro')) {
      return { available: ['prd'], disabled: [] }
    } else if (lowerAccountName.includes('-tst') || lowerAccountName.includes('_tst')) {
      // TST accounts can have both QA and UAT
      const allTstEnvs = ['qa', 'uat']
      const disabled = onboardedEnvironments.filter(env => allTstEnvs.includes(env))
      return { available: allTstEnvs, disabled }
    } else {
      return { available: ['dev', 'qa', 'uat', 'prd'], disabled: [] }
    }
  }

  const loadProductDLs = async (productCode) => {
    try {
      setLoadingTeamInfo(true)
      const response = await productDlApi.getAll({ product_code: productCode })
      const dls = response.data.items || []
      
      if (dls.length > 0) {
        const dl = dls[0]
        setHasProductDLs(true)
        setFormData(prev => ({
          ...prev,
          developmentTeamDL: dl.development_team_dl || '',
        }))
      } else {
        setHasProductDLs(false)
        setFormData(prev => ({
          ...prev,
          developmentTeamDL: '',
        }))
      }
    } catch (error) {
      console.error('Error loading team distribution lists:', error)
      setHasProductDLs(false)
      setFormData(prev => ({
        ...prev,
        developmentTeamDL: '',
      }))
    } finally {
      setLoadingTeamInfo(false)
    }
  }

  const handleDLSubmit = async (dlData) => {
    try {
      setLoadingDLSubmit(true)
      
      const productDlData = {
        product_code: formData.productCode,
        development_team_dl: dlData.developmentTeamDL,
        additional_dl: dlData.additionalDL || null,
      }

      await productDlApi.create(productDlData)
      
      setSnackbar({ 
        open: true, 
        message: 'Product distribution lists saved successfully!', 
        severity: 'success' 
      })
      
      // Reload the DLs to update the form
      await loadProductDLs(formData.productCode)
    } catch (error) {
      console.error('Error saving product DLs:', error)
      const errorMessage = error.response?.data?.detail || 'Error saving distribution lists. Please try again.'
      setSnackbar({ open: true, message: errorMessage, severity: 'error' })
    } finally {
      setLoadingDLSubmit(false)
    }
  }

  const validateForm = () => {
    if (!formData.productName) {
      setSnackbar({ open: true, message: 'Please select a product', severity: 'error' })
      return false
    }
    if (!formData.awsAccountName) {
      setSnackbar({ open: true, message: 'Please select an AWS account', severity: 'error' })
      return false
    }
    if (!formData.productEnv) {
      setSnackbar({ open: true, message: 'Please select an environment', severity: 'error' })
      return false
    }
    return true
  }

  const validateEmailList = (emailString) => {
    if (!emailString || emailString.trim() === '') return { valid: true, message: '' }
    
    const emails = emailString.split(',').map(email => email.trim())
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    
    for (let email of emails) {
      if (!emailRegex.test(email)) {
        return { valid: false, message: `Invalid email format: ${email}` }
      }
    }
    
    return { valid: true, message: '' }
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }
    
    setLoading(true)
    try {
      // Step 1: Save distribution lists if they don't exist
      if (!hasProductDLs) {
        // Validate DL fields before submitting
        if (!formData.developmentTeamDL) {
          setSnackbar({ open: true, message: 'Please fill out all required distribution list fields', severity: 'error' })
          setLoading(false)
          return
        }

        // Validate email formats for all fields
        const devTeamValidation = validateEmailList(formData.developmentTeamDL)
        if (!devTeamValidation.valid) {
          setSnackbar({ open: true, message: `Development Team DL: ${devTeamValidation.message}`, severity: 'error' })
          setLoading(false)
          return
        }

        // Additional DL is optional, but if provided, must be valid
        if (formData.additionalDL) {
          const additionalValidation = validateEmailList(formData.additionalDL)
          if (!additionalValidation.valid) {
            setSnackbar({ open: true, message: `Additional DL: ${additionalValidation.message}`, severity: 'error' })
            setLoading(false)
            return
          }
        }

        const productDlData = {
          product_code: formData.productCode,
          development_team_dl: formData.developmentTeamDL,
          additional_dl: formData.additionalDL || null,
        }
        
        await productDlApi.create(productDlData)
      }
      
      // Step 2: Create AWS Certificate Account onboarding record
      // API key will be auto-generated by the backend
      const onboardingData = {
        product_env: formData.productEnv,
        aws_account_number: formData.awsAccountNumber,
      }
      
      const response = await awsCertAccountApi.create(onboardingData)
      
      // Capture the generated client credentials from response (shown once)
      setGeneratedApiKey(response.data.raw_client_secret)
      setGeneratedClientId(response.data.client_id)
      
      setSnackbar({ open: true, message: 'AWS Account onboarded successfully!', severity: 'success' })
      setSubmitted(true)
      
      // Call onSuccess callback if provided (for modal usage)
      if (onSuccess) {
        onSuccess(response.data)
      }
    } catch (error) {
      console.error('Error onboarding account:', error)
      const errorMessage = error.response?.data?.detail || 'Error onboarding account. Please try again.'
      setSnackbar({ open: true, message: errorMessage, severity: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleProductChange = (event, newValue) => {
    setFormData({
      ...formData,
      productCode: newValue?.product_code || '',
      productName: newValue?.product_name || '',
      awsAccountName: '',
      awsAccountNumber: '',
      applicationId: '',
    })
  }

  const handleAccountChange = (event) => {
    const selectedAccount = awsAccounts.find(acc => acc.account_name === event.target.value)
    const accountName = event.target.value
    
    // Determine available environments based on account name and onboarded environments
    const { available, disabled } = determineAvailableEnvironments(
      accountName, 
      selectedAccount?.onboarded_environments || []
    )
    setAvailableEnvironments(available)
    setDisabledEnvironments(disabled)
    
    // Auto-select environment if only one option available that's not disabled
    const selectableEnvs = available.filter(env => !disabled.includes(env))
    const newEnv = selectableEnvs.length === 1 ? selectableEnvs[0] : ''
    
    setFormData({
      ...formData,
      awsAccountName: accountName,
      awsAccountNumber: selectedAccount?.account_number || '',
      applicationId: selectedAccount?.application_id || '',
      productEnv: newEnv,
    })
    
    // Load team distribution lists
    if (formData.productCode) {
      loadProductDLs(formData.productCode)
    }
  }

  const getEnvironmentLabel = (env) => {
    switch (env) {
      case 'prd': return 'Production'
      case 'uat': return 'User Acceptance'
      case 'qa': return 'Quality Assurance'
      case 'dev': return 'Development'
      default: return env.toUpperCase()
    }
  }

  return (
    <Box sx={{ maxWidth: isModal ? '100%' : 900, mx: isModal ? 0 : 'auto' }}>
      {!isModal && (
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
          AWS Account Onboarding
        </Typography>
      )}
      
      <Paper elevation={isModal ? 0 : 2} sx={{ p: isModal ? 0 : 4, borderRadius: 2 }}>
        {submitted ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Onboarding Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your AWS account has been successfully onboarded.
            </Typography>
            
            {generatedApiKey && (
              <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Important: Save Your Client Credentials
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  These credentials will only be shown once. Please store them as a Secrets Manager secret in AWS account <strong>{formData.awsAccountName}</strong>.
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Create the following Secrets Manager entry in <strong>{formData.awsAccountName}</strong>:
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {[
                    { label: 'Secret Name', value: `certificateManagement/${formData.productEnv}/api-key` },
                    { label: 'client_id', value: generatedClientId },
                    { label: 'client_secret', value: generatedApiKey },
                  ].map(({ label, value }) => (
                    <Grid item xs={12} key={label}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 110, fontWeight: 'medium' }}>
                          {label}:
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            bgcolor: 'grey.100',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            wordBreak: 'break-all',
                            flex: 1,
                          }}
                        >
                          {value}
                        </Paper>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => {
                            navigator.clipboard.writeText(value)
                            setSnackbar({ open: true, message: `${label} copied to clipboard!`, severity: 'success' })
                          }}
                        >
                          Copy
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudUploadIcon color="primary" />
                  Account Details
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    {/* Info alert when product is pre-selected */}
                    {preSelectedProduct && (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        Onboarding account for: <strong>{preSelectedProduct.product_name}</strong>
                      </Alert>
                    )}
                    
                    {/* Product Name - Always visible */}
                    <Autocomplete
                      fullWidth
                      options={itsmProducts}
                      getOptionLabel={(option) => option.product_name}
                      value={preSelectedProduct || itsmProducts.find(p => p.product_code === formData.productCode) || null}
                      onChange={handleProductChange}
                      onInputChange={(event, newInputValue) => {
                        setProductSearchText(newInputValue)
                      }}
                      loading={loadingProducts}
                      disabled={!!preSelectedProduct}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Product Name *"
                          placeholder="Search products..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ mb: 3 }}
                      isOptionEqualToValue={(option, value) => option.product_code === value.product_code}
                    />

                    {/* AWS Account Name - Visible after product selected */}
                    {(formData.productCode || preSelectedProduct) && (
                      <Fade in={true}>
                        <FormControl fullWidth sx={{ mb: 3 }} disabled={loadingAccounts}>
                          <InputLabel>AWS Account Name *</InputLabel>
                          <Select
                            value={formData.awsAccountName}
                            onChange={handleAccountChange}
                            label="AWS Account Name *"
                          >
                            {loadingAccounts ? (
                              <MenuItem disabled>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Loading accounts...
                              </MenuItem>
                            ) : awsAccounts.length === 0 ? (
                              <MenuItem disabled>
                                {useAvailableAccountsOnly 
                                  ? 'No available AWS accounts to onboard' 
                                  : 'No AWS accounts found for this product'}
                              </MenuItem>
                            ) : (
                              awsAccounts.map((account) => (
                                <MenuItem key={account.account_number} value={account.account_name}>
                                  {account.account_name}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Fade>
                    )}

                    {/* Auto-populated fields - Visible after account selected */}
                    {formData.awsAccountName && (
                      <Fade in={true}>
                        <Box>
                          <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Product Code"
                                value={formData.productCode}
                                InputProps={{ readOnly: true }}
                                helperText="Product catalog ID"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="AWS Account Number"
                                value={formData.awsAccountNumber}
                                InputProps={{ readOnly: true }}
                                helperText="Auto-populated"
                              />
                            </Grid>
                          </Grid>

                          <TextField
                            fullWidth
                            label="Application ID"
                            value={formData.applicationId}
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 3 }}
                            helperText="Application UUID (brcp:application:id)"
                          />
                        </Box>
                      </Fade>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Environment Configuration - Visible after account selected */}
            {formData.awsAccountName && (
              <Fade in={true}>
                <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Environment Configuration
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    
                    <FormControl component="fieldset">
                      <FormLabel component="legend" sx={{ mb: 2 }}>
                        Select Environment *
                      </FormLabel>
                      <RadioGroup
                        row
                        value={formData.productEnv}
                        onChange={(e) => setFormData({ ...formData, productEnv: e.target.value })}
                      >
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {availableEnvironments.map((env) => {
                            const isDisabled = disabledEnvironments.includes(env)
                            const isSelected = formData.productEnv === env
                            
                            return (
                              <Paper
                                key={env}
                                elevation={isSelected ? 3 : 1}
                                sx={{
                                  p: 2,
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  border: isSelected ? '2px solid' : '1px solid',
                                  borderColor: isSelected ? 'primary.main' : isDisabled ? 'grey.400' : 'grey.300',
                                  minWidth: 120,
                                  textAlign: 'center',
                                  transition: 'all 0.2s',
                                  opacity: isDisabled ? 0.5 : 1,
                                  bgcolor: isDisabled ? 'grey.100' : 'background.paper',
                                  '&:hover': {
                                    borderColor: isDisabled ? 'grey.400' : 'primary.main',
                                    transform: isDisabled ? 'none' : 'translateY(-2px)',
                                  }
                                }}
                                onClick={() => !isDisabled && setFormData({ ...formData, productEnv: env })}
                              >
                                <Radio
                                  checked={isSelected}
                                  value={env}
                                  disabled={isDisabled}
                                  sx={{ display: 'none' }}
                                />
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    textTransform: 'uppercase',
                                    color: isDisabled ? 'text.disabled' : 'inherit'
                                  }}
                                >
                                  {env}
                                </Typography>
                                <Chip
                                  label={isDisabled ? 'Already Onboarded' : getEnvironmentLabel(env)}
                                  size="small"
                                  sx={{ mt: 1 }}
                                  color={isSelected ? 'primary' : isDisabled ? 'default' : 'default'}
                                />
                              </Paper>
                            )
                          })}
                        </Box>
                      </RadioGroup>
                    </FormControl>
                  </CardContent>
                </Card>
              </Fade>
            )}

            {/* Team Information - Visible after account selected */}
            {formData.awsAccountName && (
              <Fade in={true}>
                <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon color="primary" />
                      Team Distribution Lists
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    
                    {loadingTeamInfo ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : hasProductDLs ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Team distribution lists auto-populated from product configuration.
                        </Typography>

                        <TeamDistributionListsForm
                          initialData={{
                            developmentTeamDL: formData.developmentTeamDL,
                            additionalDL: ''
                          }}
                          readOnly={true}
                        />
                      </>
                    ) : (
                      <>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Looks like this product hasn't been onboarded yet.
                          </Typography>
                          <Typography variant="body2">
                            Please fill out the form below to register the team distribution lists for this product.
                          </Typography>
                        </Alert>

                        <TeamDistributionListsForm
                          initialData={{
                            developmentTeamDL: formData.developmentTeamDL,
                            additionalDL: formData.additionalDL
                          }}
                          onChange={(dlData) => {
                            setFormData(prev => ({
                              ...prev,
                              developmentTeamDL: dlData.developmentTeamDL,
                              additionalDL: dlData.additionalDL || '',
                            }))
                          }}
                          readOnly={false}
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              </Fade>
            )}

            {/* Submit Button - Visible when form is ready */}
            {formData.awsAccountName && formData.productEnv && (
              <Fade in={true}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={loading}
                    endIcon={<SendIcon />}
                  >
                    {loading ? 'Submitting...' : 'Submit Onboarding'}
                  </Button>
                </Box>
              </Fade>
            )}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AccountOnboard
